import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader, Copy, Check, ExternalLink } from 'lucide-react';
import { API_URL as API } from '../config/api';

const Purchase = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { currency, convertPrice, getCurrencySymbol } = useCurrency();
  const [quantity, setQuantity] = useState(1);
  const [affiliateCode, setAffiliateCode] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  
  // Crypto payment state
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [selectedCrypto, setSelectedCrypto] = useState('usdttrc20');
  const [cryptoPayment, setCryptoPayment] = useState(null);
  const [cryptoStatus, setCryptoStatus] = useState('waiting');
  const [copied, setCopied] = useState(false);
  const [availableCryptos, setAvailableCryptos] = useState([]);

  const sessionId = searchParams.get('session_id');

  // Translations for this page
  const pageTexts = {
    fr: {
      verifyingPayment: 'Vérification du paiement en cours...',
      paymentTimeout: 'Vérification du paiement expirée. Veuillez vérifier votre tableau de bord.',
      paymentExpired: 'Session de paiement expirée. Veuillez réessayer.',
      paymentStatusError: 'Erreur lors de la vérification du paiement. Veuillez réessayer.',
      paymentComplete: 'Paiement effectué !',
      cardIntl: 'Carte bancaire internationale',
      mobileHaiti: 'Paiement mobile Haïti',
      cryptoDesc: 'Bitcoin, Ethereum, Tether USDT',
      securePayment: 'Paiement sécurisé - Toutes les transactions sont encryptées',
      ticket: 'billet',
      tickets: 'billets',
      equivalent: 'Équivalent',
      proceedPayment: 'Procéder au paiement',
      selectCrypto: 'Choisir la crypto',
      sendPayment: 'Envoyez exactement ce montant:',
      toAddress: 'À cette adresse:',
      copyAddress: 'Copier',
      copied: 'Copié!',
      waitingPayment: 'En attente du paiement...',
      paymentConfirming: 'Paiement détecté, confirmation en cours...',
      paymentConfirmed: 'Paiement confirmé!',
      cancelCrypto: 'Annuler'
    },
    en: {
      verifyingPayment: 'Verifying payment...',
      paymentTimeout: 'Payment verification timed out. Please check your dashboard.',
      paymentExpired: 'Payment session expired. Please try again.',
      paymentStatusError: 'Error checking payment status. Please try again.',
      paymentComplete: 'Payment completed!',
      cardIntl: 'International credit card',
      mobileHaiti: 'Haiti mobile payment',
      cryptoDesc: 'Bitcoin, Ethereum, Tether USDT',
      securePayment: 'Secure payment - All transactions are encrypted',
      ticket: 'ticket',
      tickets: 'tickets',
      equivalent: 'Equivalent',
      proceedPayment: 'Proceed to payment',
      selectCrypto: 'Select cryptocurrency',
      sendPayment: 'Send exactly this amount:',
      toAddress: 'To this address:',
      copyAddress: 'Copy',
      copied: 'Copied!',
      waitingPayment: 'Waiting for payment...',
      paymentConfirming: 'Payment detected, confirming...',
      paymentConfirmed: 'Payment confirmed!',
      cancelCrypto: 'Cancel'
    },
    ht: {
      verifyingPayment: 'Verifye peman...',
      paymentTimeout: 'Verifikasyon peman ekspire. Tanpri tcheke tablo bò ou.',
      paymentExpired: 'Sesyon peman ekspire. Tanpri eseye ankò.',
      paymentStatusError: 'Erè nan verifye estati peman. Tanpri eseye ankò.',
      paymentComplete: 'Peman reyisi!',
      cardIntl: 'Kat kredi entènasyonal',
      mobileHaiti: 'Peman mobil Ayiti',
      cryptoDesc: 'Bitcoin, Ethereum, Tether USDT',
      securePayment: 'Peman sekirize - Tout tranzaksyon yo kripte',
      ticket: 'tikè',
      tickets: 'tikè',
      equivalent: 'Ekivalan',
      proceedPayment: 'Kontinye ak peman',
      selectCrypto: 'Chwazi crypto',
      sendPayment: 'Voye egzakteman kantite sa a:',
      toAddress: 'Nan adrès sa a:',
      copyAddress: 'Kopye',
      copied: 'Kopye!',
      waitingPayment: 'Ap tann peman...',
      paymentConfirming: 'Peman detekte, konfime...',
      paymentConfirmed: 'Peman konfime!',
      cancelCrypto: 'Anile'
    },
    es: {
      verifyingPayment: 'Verificando pago...',
      paymentTimeout: 'Verificación de pago expirada. Por favor revise su panel.',
      paymentExpired: 'Sesión de pago expirada. Por favor intente de nuevo.',
      paymentStatusError: 'Error al verificar el estado del pago. Por favor intente de nuevo.',
      paymentComplete: '¡Pago completado!',
      cardIntl: 'Tarjeta de crédito internacional',
      mobileHaiti: 'Pago móvil Haití',
      cryptoDesc: 'Bitcoin, Ethereum, Tether USDT',
      securePayment: 'Pago seguro - Todas las transacciones están encriptadas',
      ticket: 'boleto',
      tickets: 'boletos',
      equivalent: 'Equivalente',
      proceedPayment: 'Proceder al pago',
      selectCrypto: 'Seleccionar crypto',
      sendPayment: 'Enviar exactamente esta cantidad:',
      toAddress: 'A esta dirección:',
      copyAddress: 'Copiar',
      copied: '¡Copiado!',
      waitingPayment: 'Esperando pago...',
      paymentConfirming: 'Pago detectado, confirmando...',
      paymentConfirmed: '¡Pago confirmado!',
      cancelCrypto: 'Cancelar'
    },
    pt: {
      verifyingPayment: 'Verificando pagamento...',
      paymentTimeout: 'Verificação de pagamento expirada. Por favor verifique seu painel.',
      paymentExpired: 'Sessão de pagamento expirada. Por favor tente novamente.',
      paymentStatusError: 'Erro ao verificar status do pagamento. Por favor tente novamente.',
      paymentComplete: 'Pagamento concluído!',
      cardIntl: 'Cartão de crédito internacional',
      mobileHaiti: 'Pagamento móvel Haiti',
      cryptoDesc: 'Bitcoin, Ethereum, Tether USDT',
      securePayment: 'Pagamento seguro - Todas as transações são criptografadas',
      ticket: 'bilhete',
      tickets: 'bilhetes',
      equivalent: 'Equivalente',
      proceedPayment: 'Prosseguir para pagamento',
      selectCrypto: 'Selecionar criptomoeda',
      sendPayment: 'Envie exatamente este valor:',
      toAddress: 'Para este endereço:',
      copyAddress: 'Copiar',
      copied: 'Copiado!',
      waitingPayment: 'Aguardando pagamento...',
      paymentConfirming: 'Pagamento detectado, confirmando...',
      paymentConfirmed: 'Pagamento confirmado!',
      cancelCrypto: 'Cancelar'
    }
  };

  const pt = pageTexts[i18n.language] || pageTexts.fr;

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error(pt.paymentTimeout);
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
        withCredentials: true,
      });

      if (response.data.payment_status === 'paid') {
        toast.success(t('common.success') + ' - ' + pt.paymentComplete);
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      } else if (response.data.status === 'expired') {
        toast.error(pt.paymentExpired);
        setCheckingPayment(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error(pt.paymentStatusError);
      setCheckingPayment(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // Fetch available cryptocurrencies
  useEffect(() => {
    const fetchCryptos = async () => {
      try {
        const response = await axios.get(`${API}/crypto/currencies`);
        setAvailableCryptos(response.data.currencies || []);
      } catch (error) {
        console.error('Failed to fetch cryptos:', error);
        setAvailableCryptos([
          { code: 'usdttrc20', name: 'USDT (TRC20)' },
          { code: 'btc', name: 'Bitcoin (BTC)' },
          { code: 'eth', name: 'Ethereum (ETH)' }
        ]);
      }
    };
    fetchCryptos();
  }, []);

  // Poll crypto payment status
  useEffect(() => {
    let interval;
    if (cryptoPayment && cryptoStatus !== 'finished' && cryptoStatus !== 'failed') {
      interval = setInterval(async () => {
        try {
          const response = await axios.get(`${API}/crypto/payment/${cryptoPayment.payment_id}/status`);
          setCryptoStatus(response.data.status);
          if (response.data.status === 'finished') {
            toast.success(pt.paymentConfirmed);
            setTimeout(() => {
              setShowCryptoModal(false);
              navigate('/dashboard');
            }, 2000);
          } else if (response.data.status === 'failed' || response.data.status === 'expired') {
            toast.error('Paiement expiré ou échoué');
            setCryptoPayment(null);
            setShowCryptoModal(false);
          }
        } catch (error) {
          console.error('Error polling crypto status:', error);
        }
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [cryptoPayment, cryptoStatus, navigate, pt.paymentConfirmed]);

  const calculatePrice = (qty) => {
    if (qty >= 3) {
      return Math.floor(qty / 3) * 100 + (qty % 3) * 50;
    }
    return qty * 50;
  };

  const totalPrice = calculatePrice(quantity);
  const convertedPrice = convertPrice(totalPrice);

  const handleCryptoPayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/crypto/purchase`, {
        price_amount: totalPrice,
        pay_currency: selectedCrypto,
        quantity,
        user_id: user?.user_id || null,
        email: user?.email || null,
        affiliate_code: affiliateCode || null,
        origin_url: window.location.origin
      });
      
      setCryptoPayment(response.data);
      setCryptoStatus('waiting');
      setShowCryptoModal(true);
    } catch (error) {
      console.error('Crypto payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement crypto');
    } finally {
      setLoading(false);
    }
  };

  // State for mobile payment modal
  const [showMobilePaymentModal, setShowMobilePaymentModal] = useState(false);
  const [mobilePaymentCopied, setMobilePaymentCopied] = useState(false);
  const [mobilePaymentSubmitting, setMobilePaymentSubmitting] = useState(false);
  const [mobilePaymentConfirmed, setMobilePaymentConfirmed] = useState(false);

  // Payment contact info
  const MONCASH_NUMBER = '+509-4460-0357';
  const NATCASH_NUMBER = '+509-5608-8028';
  const WHATSAPP_NUMBER = '50939451425'; // Haiti Digi number for WhatsApp contact
  const PAYPAL_EMAIL = 'marathonedenlife@gmail.com';
  const INTERAC_EMAIL = 'marathonedenlife@gmail.com';

  // Function to submit mobile payment request
  const submitMobilePaymentRequest = async () => {
    if (!user?.email) {
      toast.error('Veuillez vous connecter pour continuer');
      return;
    }
    
    setMobilePaymentSubmitting(true);
    try {
      const response = await axios.post(`${API}/mobile-payment/request`, {
        payment_type: 'purchase',
        payment_method: paymentMethod,
        amount: totalPrice,
        quantity: quantity,
        email: user.email,
        name: user.name,
        affiliate_code: affiliateCode || null,
        language: i18n.language
      });
      
      if (response.data.success) {
        setMobilePaymentConfirmed(true);
        toast.success('✅ Demande enregistrée ! Un email de confirmation vous a été envoyé.');
      }
    } catch (error) {
      console.error('Mobile payment request error:', error);
      toast.error('Erreur lors de l\'enregistrement de la demande');
    } finally {
      setMobilePaymentSubmitting(false);
    }
  };

  // Mobile/Manual payment translations
  const mobilePaymentTexts = {
    fr: {
      title: 'Paiement Mobile',
      instructions: 'Instructions de paiement',
      step1: 'Ouvrez votre application',
      step2: 'Envoyez le montant exact au numéro ci-dessous',
      step3: 'Dans la note/référence, écrivez votre EMAIL',
      step4: 'Une fois le paiement confirmé, vos billets seront générés sous 24h',
      amount: 'Montant à envoyer',
      amountUSD: 'Montant en USD',
      amountHTG: 'Montant en Gourdes (HTG)',
      amountCAD: 'Montant en CAD (Interac)',
      phoneNumber: 'Numéro de téléphone',
      emailAddress: 'Adresse email',
      reference: 'Référence (votre email)',
      important: 'IMPORTANT',
      importantNote: 'Après avoir effectué le paiement, veuillez nous contacter sur WhatsApp avec la capture d\'écran de la transaction pour accélérer le traitement.',
      whatsappContact: 'Contacter sur WhatsApp',
      close: 'Fermer',
      copyNumber: 'Copier',
      confirmRequest: 'Recevoir les instructions par email',
      confirming: 'Envoi en cours...',
      requestConfirmed: '✅ Demande enregistrée !',
      emailSent: 'Un email de confirmation avec les instructions vous a été envoyé.',
      // Moncash/Natcash specific
      moncashNote: 'Le montant sera converti en Gourdes Haïtiennes (HTG)',
      natcashNote: 'Le montant sera converti en Gourdes Haïtiennes (HTG)',
      haitiOnly: 'Haïti seulement',
      // PayPal specific
      paypalTitle: 'Paiement PayPal',
      paypalStep1: 'Connectez-vous à votre compte PayPal',
      paypalStep2: 'Cliquez sur "Envoyer de l\'argent"',
      paypalStep3: 'Envoyez le montant exact à l\'adresse ci-dessous',
      paypalStep4: 'Ajoutez votre email en note pour identification',
      // Interac specific
      interacTitle: 'Virement Interac',
      interacStep1: 'Accédez à votre banque en ligne',
      interacStep2: 'Sélectionnez "Virement Interac"',
      interacStep3: 'Envoyez le montant exact à l\'adresse ci-dessous',
      interacStep4: 'Ajoutez votre email dans le message',
      interacNote: 'Le montant sera converti en dollars canadiens (CAD)',
      canadaOnly: 'Canada seulement'
    },
    en: {
      title: 'Mobile Payment',
      instructions: 'Payment Instructions',
      step1: 'Open your app',
      step2: 'Send the exact amount to the number below',
      step3: 'In the note/reference, write your EMAIL',
      step4: 'Once payment is confirmed, your tickets will be generated within 24h',
      amount: 'Amount to send',
      amountUSD: 'Amount in USD',
      amountHTG: 'Amount in Gourdes (HTG)',
      amountCAD: 'Amount in CAD (Interac)',
      phoneNumber: 'Phone number',
      emailAddress: 'Email address',
      reference: 'Reference (your email)',
      important: 'IMPORTANT',
      importantNote: 'After making the payment, please contact us on WhatsApp with the transaction screenshot to speed up processing.',
      whatsappContact: 'Contact on WhatsApp',
      close: 'Close',
      copyNumber: 'Copy',
      confirmRequest: 'Receive instructions by email',
      confirming: 'Sending...',
      requestConfirmed: '✅ Request registered!',
      emailSent: 'A confirmation email with instructions has been sent to you.',
      // Moncash/Natcash specific
      moncashNote: 'Amount will be converted to Haitian Gourdes (HTG)',
      natcashNote: 'Amount will be converted to Haitian Gourdes (HTG)',
      haitiOnly: 'Haiti only',
      // PayPal specific
      paypalTitle: 'PayPal Payment',
      paypalStep1: 'Log in to your PayPal account',
      paypalStep2: 'Click on "Send Money"',
      paypalStep3: 'Send the exact amount to the address below',
      paypalStep4: 'Add your email in the note for identification',
      // Interac specific
      interacTitle: 'Interac e-Transfer',
      interacStep1: 'Access your online banking',
      interacStep2: 'Select "Interac e-Transfer"',
      interacStep3: 'Send the exact amount to the address below',
      interacStep4: 'Add your email in the message',
      interacNote: 'Amount will be converted to Canadian dollars (CAD)',
      canadaOnly: 'Canada only'
    },
    ht: {
      title: 'Peman Mobil',
      instructions: 'Enstriksyon pou peman',
      step1: 'Ouvri aplikasyon ou',
      step2: 'Voye kantite egzak la nan nimewo ki anba a',
      step3: 'Nan nòt/referans, ekri EMAIL ou',
      step4: 'Yon fwa peman an konfime, tikè ou yo ap jenere nan 24è',
      amount: 'Kantite pou voye',
      amountUSD: 'Kantite an USD',
      amountHTG: 'Kantite an Goud (HTG)',
      amountCAD: 'Kantite an CAD (Interac)',
      phoneNumber: 'Nimewo telefòn',
      emailAddress: 'Adrès imèl',
      reference: 'Referans (imèl ou)',
      important: 'ENPÒTAN',
      importantNote: 'Apre ou fin fè peman an, tanpri kontakte nou sou WhatsApp ak foto tranzaksyon an pou akselere tretman an.',
      whatsappContact: 'Kontakte sou WhatsApp',
      close: 'Fèmen',
      copyNumber: 'Kopye',
      confirmRequest: 'Resevwa enstriksyon pa imèl',
      confirming: 'Ap voye...',
      requestConfirmed: '✅ Demann anrejistre!',
      emailSent: 'Yon imèl konfimasyon ak enstriksyon voye ba ou.',
      // Moncash/Natcash specific
      moncashNote: 'Kantite a ap konvèti an Goud Ayisyen (HTG)',
      natcashNote: 'Kantite a ap konvèti an Goud Ayisyen (HTG)',
      haitiOnly: 'Ayiti sèlman',
      // PayPal specific
      paypalTitle: 'Peman PayPal',
      paypalStep1: 'Konekte nan kont PayPal ou',
      paypalStep2: 'Klike sou "Voye Lajan"',
      paypalStep3: 'Voye kantite egzak la nan adrès ki anba a',
      paypalStep4: 'Ajoute imèl ou nan nòt pou idantifikasyon',
      // Interac specific
      interacTitle: 'Transfè Interac',
      interacStep1: 'Ale nan bank anliy ou',
      interacStep2: 'Chwazi "Transfè Interac"',
      interacStep3: 'Voye kantite egzak la nan adrès ki anba a',
      interacStep4: 'Ajoute imèl ou nan mesaj la',
      interacNote: 'Kantite a ap konvèti an dola Kanadyen (CAD)',
      canadaOnly: 'Kanada sèlman'
    },
    es: {
      title: 'Pago Móvil',
      instructions: 'Instrucciones de pago',
      step1: 'Abra su aplicación',
      step2: 'Envíe el monto exacto al número siguiente',
      step3: 'En la nota/referencia, escriba su EMAIL',
      step4: 'Una vez confirmado el pago, sus boletos se generarán en 24h',
      amount: 'Monto a enviar',
      amountUSD: 'Monto en USD',
      amountHTG: 'Monto en Gourdes (HTG)',
      amountCAD: 'Monto en CAD (Interac)',
      phoneNumber: 'Número de teléfono',
      emailAddress: 'Dirección de email',
      reference: 'Referencia (su email)',
      important: 'IMPORTANTE',
      importantNote: 'Después de realizar el pago, contáctenos en WhatsApp con la captura de pantalla de la transacción para acelerar el procesamiento.',
      whatsappContact: 'Contactar en WhatsApp',
      close: 'Cerrar',
      copyNumber: 'Copiar',
      confirmRequest: 'Recibir instrucciones por email',
      confirming: 'Enviando...',
      requestConfirmed: '✅ ¡Solicitud registrada!',
      emailSent: 'Se le ha enviado un email de confirmación con las instrucciones.',
      // Moncash/Natcash specific
      moncashNote: 'El monto se convertirá a Gourdes Haitianos (HTG)',
      natcashNote: 'El monto se convertirá a Gourdes Haitianos (HTG)',
      haitiOnly: 'Solo Haití',
      // PayPal specific
      paypalTitle: 'Pago PayPal',
      paypalStep1: 'Inicie sesión en su cuenta PayPal',
      paypalStep2: 'Haga clic en "Enviar dinero"',
      paypalStep3: 'Envíe el monto exacto a la dirección siguiente',
      paypalStep4: 'Agregue su email en la nota para identificación',
      // Interac specific
      interacTitle: 'Transferencia Interac',
      interacStep1: 'Acceda a su banca en línea',
      interacStep2: 'Seleccione "Transferencia Interac"',
      interacStep3: 'Envíe el monto exacto a la dirección siguiente',
      interacStep4: 'Agregue su email en el mensaje',
      interacNote: 'El monto se convertirá a dólares canadienses (CAD)',
      canadaOnly: 'Solo Canadá'
    },
    pt: {
      title: 'Pagamento Móvel',
      instructions: 'Instruções de pagamento',
      step1: 'Abra seu aplicativo',
      step2: 'Envie o valor exato para o número abaixo',
      step3: 'Na nota/referência, escreva seu EMAIL',
      step4: 'Após confirmação do pagamento, seus bilhetes serão gerados em 24h',
      amount: 'Valor a enviar',
      amountUSD: 'Valor em USD',
      amountHTG: 'Valor em Gourdes (HTG)',
      amountCAD: 'Valor em CAD (Interac)',
      phoneNumber: 'Número de telefone',
      emailAddress: 'Endereço de email',
      reference: 'Referência (seu email)',
      important: 'IMPORTANTE',
      importantNote: 'Após efetuar o pagamento, entre em contato conosco no WhatsApp com a captura de tela da transação para acelerar o processamento.',
      whatsappContact: 'Contatar no WhatsApp',
      close: 'Fechar',
      copyNumber: 'Copiar',
      confirmRequest: 'Receber instruções por email',
      confirming: 'Enviando...',
      requestConfirmed: '✅ Solicitação registrada!',
      emailSent: 'Um email de confirmação com as instruções foi enviado para você.',
      // Moncash/Natcash specific
      moncashNote: 'O valor será convertido para Gourdes Haitianos (HTG)',
      natcashNote: 'O valor será convertido para Gourdes Haitianos (HTG)',
      haitiOnly: 'Apenas Haiti',
      // PayPal specific
      paypalTitle: 'Pagamento PayPal',
      paypalStep1: 'Faça login na sua conta PayPal',
      paypalStep2: 'Clique em "Enviar dinheiro"',
      paypalStep3: 'Envie o valor exato para o endereço abaixo',
      paypalStep4: 'Adicione seu email na nota para identificação',
      // Interac specific
      interacTitle: 'Transferência Interac',
      interacStep1: 'Acesse seu banco online',
      interacStep2: 'Selecione "Transferência Interac"',
      interacStep3: 'Envie o valor exato para o endereço abaixo',
      interacStep4: 'Adicione seu email na mensagem',
      interacNote: 'O valor será convertido para dólares canadenses (CAD)',
      canadaOnly: 'Apenas Canadá'
    }
  };

  const mpt = mobilePaymentTexts[i18n.language] || mobilePaymentTexts.fr;

  // HTG conversion rate (approximately 1 USD = 135 HTG)
  const HTG_RATE = 135;
  const convertToHTG = (usdAmount) => Math.round(usdAmount * HTG_RATE).toLocaleString();

  // CAD conversion rate (approximately 1 USD = 1.35 CAD)
  const CAD_RATE = 1.35;
  const convertToCAD = (usdAmount) => (usdAmount * CAD_RATE).toFixed(2);

  const handleMobilePayment = (method) => {
    setPaymentMethod(method);
    setShowMobilePaymentModal(true);
    setMobilePaymentConfirmed(false); // Reset confirmation state
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text.replace(/-/g, ''));
    setMobilePaymentCopied(true);
    setTimeout(() => setMobilePaymentCopied(false), 2000);
    toast.success(mpt.copyNumber + ' ✓');
  };

  const copyMobileNumber = (number) => {
    copyToClipboard(number);
  };

  // Get payment info based on method
  const getPaymentInfo = () => {
    switch(paymentMethod) {
      case 'moncash':
        return { type: 'phone', value: MONCASH_NUMBER, label: 'Moncash', color: 'from-yellow-500 to-orange-500' };
      case 'natcash':
        return { type: 'phone', value: NATCASH_NUMBER, label: 'Natcash', color: 'from-blue-500 to-blue-600' };
      case 'paypal':
        return { type: 'email', value: PAYPAL_EMAIL, label: 'PayPal', color: 'from-blue-600 to-blue-800' };
      case 'interac':
        return { type: 'email', value: INTERAC_EMAIL, label: 'Virement Interac', color: 'from-yellow-500 to-yellow-600' };
      default:
        return { type: 'phone', value: '', label: '', color: 'from-gray-500 to-gray-600' };
    }
  };

  const handlePurchase = async () => {
    // Handle manual payment methods - show modal
    if (['moncash', 'natcash', 'paypal', 'interac'].includes(paymentMethod)) {
      setShowMobilePaymentModal(true);
      return;
    }

    // Handle crypto payment separately
    if (paymentMethod === 'crypto') {
      await handleCryptoPayment();
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      // Stripe card payment
      const response = await axios.post(
        `${API}/payments/checkout/session`,
        {
          quantity,
          origin_url: originUrl,
          affiliate_code: affiliateCode || null,
        },
        { withCredentials: true }
      );

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
      setLoading(false);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-12 max-w-md text-center"
        >
          <Loader className="w-16 h-16 text-primary mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold text-primary mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('common.loading')}
          </h2>
          <p className="text-gray-600">{pt.verifyingPayment}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pt-24 px-6 pb-12"
      style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}
      data-testid="purchase-page"
    >
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8 md:p-12"
        >
          <h1
            className="text-4xl font-bold text-primary mb-8 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="purchase-title"
          >
            {t('tickets.title')}
          </h1>

          <div className="space-y-6">
            <div>
              <Label htmlFor="quantity" className="text-lg font-semibold">
                {t('tickets.quantity')}
              </Label>
              <div className="mt-2 flex items-center gap-4">
                <Button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  variant="outline"
                  size="lg"
                  className="w-16 h-16 rounded-full text-2xl font-bold"
                  data-testid="quantity-decrease"
                >
                  −
                </Button>
                <div className="flex-1 bg-primary/5 rounded-2xl h-16 flex items-center justify-center border-2 border-primary/20">
                  <span className="text-4xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {quantity}
                  </span>
                </div>
                <Button
                  type="button"
                  onClick={() => setQuantity(Math.min(100, quantity + 1))}
                  variant="outline"
                  size="lg"
                  className="w-16 h-16 rounded-full text-2xl font-bold"
                  data-testid="quantity-increase"
                >
                  +
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">{t('tickets.singlePrice')}</p>
              <p className="text-sm text-accent font-semibold">{t('tickets.specialOffer')}</p>
            </div>

            <div>
              <Label htmlFor="affiliate" className="text-lg font-semibold">
                {t('tickets.affiliateCode')}
              </Label>
              <Input
                id="affiliate"
                type="text"
                value={affiliateCode}
                onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                placeholder="ABC12345"
                className="mt-2 h-14 text-lg"
                data-testid="affiliate-code-input"
              />
            </div>

            <div>
              <Label htmlFor="payment" className="text-lg font-semibold">
                {t('tickets.selectPayment')}
              </Label>
              <div className="mt-4 space-y-3">
                {/* Stripe */}
                <div 
                  onClick={() => setPaymentMethod('stripe')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'stripe' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#635BFF] rounded-lg flex items-center justify-center">
                        <svg className="w-8 h-8" viewBox="0 0 28 28" fill="white">
                          <path d="M13.3 11.5c0-.6.5-1 1.2-1 1.5 0 3.4.5 4.9 1.4V8.8c-1.6-.7-3.2-1-4.9-1-4 0-6.7 2.1-6.7 5.6 0 5.4 7.5 4.5 7.5 6.8 0 .9-.8 1.2-1.9 1.2-1.6 0-3.7-.7-5.4-1.6v3.2c1.8.8 3.6 1.1 5.4 1.1 4.1 0 7-2 7-5.6 0-5.8-7.6-4.8-7.6-7z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold">Stripe</p>
                        <p className="text-xs text-gray-500">{pt.cardIntl}</p>
                      </div>
                    </div>
                    {paymentMethod === 'stripe' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* MONCASH */}
                <div 
                  onClick={() => setPaymentMethod('moncash')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'moncash' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#D32F2F] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">MC</span>
                      </div>
                      <div>
                        <p className="font-semibold">MONCASH</p>
                        <p className="text-xs text-gray-500">{pt.mobileHaiti}</p>
                      </div>
                    </div>
                    {paymentMethod === 'moncash' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* NATCASH */}
                <div 
                  onClick={() => setPaymentMethod('natcash')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'natcash' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#1976D2] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">NC</span>
                      </div>
                      <div>
                        <p className="font-semibold">NATCASH</p>
                        <p className="text-xs text-gray-500">{pt.mobileHaiti}</p>
                      </div>
                    </div>
                    {paymentMethod === 'natcash' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* PAYPAL */}
                <div 
                  onClick={() => setPaymentMethod('paypal')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'paypal' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                  data-testid="payment-paypal"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#003087] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">PP</span>
                      </div>
                      <div>
                        <p className="font-semibold">PayPal</p>
                        <p className="text-xs text-gray-500">{i18n.language === 'fr' ? 'Paiement sécurisé' : 'Secure payment'}</p>
                      </div>
                    </div>
                    {paymentMethod === 'paypal' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* INTERAC eTransfer */}
                <div 
                  onClick={() => setPaymentMethod('interac')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'interac' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                  data-testid="payment-interac"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#FFCC00] rounded-lg flex items-center justify-center">
                        <span className="text-black font-bold text-xs">e-T</span>
                      </div>
                      <div>
                        <p className="font-semibold">Virement Interac</p>
                        <p className="text-xs text-gray-500">{i18n.language === 'fr' ? 'Canada seulement' : 'Canada only'}</p>
                      </div>
                    </div>
                    {paymentMethod === 'interac' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>

                {/* CRYPTO (USDT/BTC/ETH) */}
                <div 
                  onClick={() => setPaymentMethod('crypto')}
                  className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    paymentMethod === 'crypto' 
                      ? 'border-primary bg-primary/5' 
                      : 'border-gray-200 hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#F7931A] via-[#627EEA] to-[#26A17B] rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-lg">₿</span>
                      </div>
                      <div>
                        <p className="font-semibold">Crypto (USDT/BTC/ETH)</p>
                        <p className="text-xs text-gray-500">{pt.cryptoDesc}</p>
                      </div>
                    </div>
                    {paymentMethod === 'crypto' && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Crypto selector when crypto is selected */}
                  {paymentMethod === 'crypto' && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-gray-600 mb-2">{pt.selectCrypto}</p>
                      <div className="grid grid-cols-2 gap-2">
                        {availableCryptos.map((crypto) => (
                          <button
                            key={crypto.code}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCrypto(crypto.code);
                            }}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              selectedCrypto === crypto.code
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {crypto.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {paymentMethod !== 'crypto' && (
                    <div className="mt-2 flex space-x-2">
                      <span className="px-2 py-1 bg-[#26A17B]/10 text-[#26A17B] text-xs font-medium rounded">USDT</span>
                      <span className="px-2 py-1 bg-[#F7931A]/10 text-[#F7931A] text-xs font-medium rounded">BTC</span>
                      <span className="px-2 py-1 bg-[#627EEA]/10 text-[#627EEA] text-xs font-medium rounded">ETH</span>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                💳 {pt.securePayment}
              </p>
            </div>

            <div className="bg-primary/5 rounded-xl p-6 mt-8">
              <div className="flex justify-between items-center mb-4">
                <span className="text-xl font-semibold">{t('tickets.total')}:</span>
                <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
                  ${totalPrice} USD
                </span>
              </div>
              <p className="text-sm text-gray-600">
                ({quantity} {quantity > 1 ? pt.tickets : pt.ticket} × ${totalPrice / quantity} = ${totalPrice} USD)
              </p>
              <p className="text-xs text-gray-500 mt-2">
                {pt.equivalent}: {getCurrencySymbol()}{convertedPrice} {currency}
              </p>
            </div>

            <Button
              onClick={handlePurchase}
              disabled={loading || quantity < 1}
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-16 text-xl font-bold rounded-full shadow-xl"
              data-testid="purchase-button"
            >
              {loading ? t('common.loading') : pt.proceedPayment}
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Crypto Payment Modal */}
      {showCryptoModal && cryptoPayment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
          >
            <h3 className="text-xl font-bold text-center mb-4">
              {cryptoStatus === 'waiting' ? pt.waitingPayment : 
               cryptoStatus === 'confirming' ? pt.paymentConfirming :
               cryptoStatus === 'finished' ? pt.paymentConfirmed : pt.waitingPayment}
            </h3>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">{pt.sendPayment}</p>
              <div className="flex items-center justify-between bg-white rounded-lg p-3 border">
                <span className="font-mono font-bold text-lg">{cryptoPayment.pay_amount}</span>
                <span className="text-gray-500 uppercase">{cryptoPayment.pay_currency}</span>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 mb-2">{pt.toAddress}</p>
              <div className="bg-white rounded-lg p-3 border">
                <p className="font-mono text-xs break-all mb-2">{cryptoPayment.pay_address}</p>
                <Button
                  onClick={() => copyToClipboard(cryptoPayment.pay_address)}
                  variant="outline"
                  size="sm"
                  className="w-full"
                >
                  {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                  {copied ? pt.copied : pt.copyAddress}
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-center mb-4">
              {cryptoStatus === 'waiting' && (
                <div className="flex items-center text-yellow-600">
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  <span>{pt.waitingPayment}</span>
                </div>
              )}
              {cryptoStatus === 'confirming' && (
                <div className="flex items-center text-blue-600">
                  <Loader className="w-5 h-5 mr-2 animate-spin" />
                  <span>{pt.paymentConfirming}</span>
                </div>
              )}
              {cryptoStatus === 'finished' && (
                <div className="flex items-center text-green-600">
                  <Check className="w-5 h-5 mr-2" />
                  <span>{pt.paymentConfirmed}</span>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-500 text-center mb-4">
              💰 ${totalPrice} USD = {cryptoPayment.pay_amount} {cryptoPayment.pay_currency.toUpperCase()}
            </p>

            <Button
              onClick={() => {
                setShowCryptoModal(false);
                setCryptoPayment(null);
              }}
              variant="outline"
              className="w-full"
            >
              {pt.cancelCrypto}
            </Button>
          </motion.div>
        </div>
      )}

      {/* Manual Payment Modal (Moncash/Natcash/PayPal/Interac) */}
      {showMobilePaymentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className={`p-6 text-white bg-gradient-to-r ${getPaymentInfo().color}`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">
                    {paymentMethod === 'moncash' ? 'M' : 
                     paymentMethod === 'natcash' ? 'N' : 
                     paymentMethod === 'paypal' ? 'P' : 'e'}
                  </span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">{getPaymentInfo().label}</h3>
                  <p className="text-white/80 text-sm">
                    {paymentMethod === 'paypal' ? 'PayPal Transfer' : 
                     paymentMethod === 'interac' ? 'Interac eTransfer' : 
                     mpt.title}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold mb-3">{mpt.instructions}</h4>
                <ol className="space-y-2 text-sm">
                  {paymentMethod === 'paypal' ? (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.paypalStep1}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.paypalStep2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.paypalStep3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.paypalStep4}</span>
                      </li>
                    </>
                  ) : paymentMethod === 'interac' ? (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.interacStep1}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.interacStep2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.interacStep3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-yellow-100 text-yellow-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.interacStep4}</span>
                      </li>
                    </>
                  ) : (
                    <>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                        <span>{mpt.step1} <strong>{getPaymentInfo().label}</strong></span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                        <span>{mpt.step2}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                        <span>{mpt.step3}</span>
                      </li>
                      <li className="flex gap-2">
                        <span className="w-6 h-6 bg-primary/10 text-primary rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                        <span>{mpt.step4}</span>
                      </li>
                    </>
                  )}
                </ol>
              </div>

              {/* Amount */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-sm text-green-700 mb-1">{mpt.amount}</p>
                <p className="text-3xl font-bold text-green-700">${totalPrice} USD</p>
                {['moncash', 'natcash'].includes(paymentMethod) && (
                  <p className="text-lg font-semibold text-green-600 mt-1">
                    ≈ {convertToHTG(totalPrice)} HTG
                  </p>
                )}
                {paymentMethod === 'interac' && (
                  <p className="text-lg font-semibold text-green-600 mt-1">
                    ≈ C${convertToCAD(totalPrice)} CAD
                  </p>
                )}
                <p className="text-sm text-green-600 mt-1">
                  {quantity} {quantity === 1 ? pt.ticket : pt.tickets}
                </p>
                {['moncash', 'natcash'].includes(paymentMethod) && (
                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">
                    🇭🇹 {paymentMethod === 'moncash' ? mpt.moncashNote : mpt.natcashNote} • {mpt.haitiOnly}
                  </p>
                )}
                {paymentMethod === 'interac' && (
                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">
                    🍁 {mpt.interacNote} • {mpt.canadaOnly}
                  </p>
                )}
              </div>

              {/* Contact Info (Phone or Email) */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-4">
                <p className="text-sm text-gray-600 mb-2">
                  {getPaymentInfo().type === 'email' ? mpt.emailAddress : mpt.phoneNumber}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`font-mono font-bold text-gray-900 ${getPaymentInfo().type === 'email' ? 'text-lg' : 'text-2xl'}`}>
                    {getPaymentInfo().value}
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getPaymentInfo().value)}
                    className="flex items-center gap-1"
                  >
                    {mobilePaymentCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {mobilePaymentCopied ? pt.copied : pt.copyAddress}
                  </Button>
                </div>
              </div>

              {/* Reference */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-700 mb-1">{mpt.reference}</p>
                <p className="font-mono font-bold text-blue-900">{user?.email || 'votre@email.com'}</p>
              </div>

              {/* Important Note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="font-bold text-amber-800 mb-1">⚠️ {mpt.important}</p>
                <p className="text-sm text-amber-700">{mpt.importantNote}</p>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
                    `Bonjour, je viens d'effectuer un achat de ${quantity} billet(s) ($${totalPrice} USD) via ${getPaymentInfo().label}. Mon email: ${user?.email || '(à compléter)'}. Merci de confirmer la réception.`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {mpt.whatsappContact}
                </a>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-gray-50 space-y-3">
              {/* Confirmation message if already submitted */}
              {mobilePaymentConfirmed && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-green-700 font-semibold">{mpt.requestConfirmed}</p>
                  <p className="text-green-600 text-sm">{mpt.emailSent}</p>
                </div>
              )}
              
              {/* Submit button */}
              {!mobilePaymentConfirmed && (
                <Button
                  onClick={submitMobilePaymentRequest}
                  disabled={mobilePaymentSubmitting}
                  className="w-full bg-primary hover:bg-primary/90"
                  data-testid="confirm-mobile-payment-btn"
                >
                  {mobilePaymentSubmitting ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      {mpt.confirming}
                    </>
                  ) : (
                    <>
                      📧 {mpt.confirmRequest}
                    </>
                  )}
                </Button>
              )}
              
              <Button
                onClick={() => setShowMobilePaymentModal(false)}
                variant="outline"
                className="w-full"
              >
                {mpt.close}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Purchase;
